import { useCallback, useMemo, useState } from "react";

import type { DataAssetWithManifestAndRealAssetDeploymentResponse } from "@/apiClient/models/DataAssetWithManifestAndRealAssetDeploymentResponse";
import type { SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_ } from "@/apiClient/models/SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_";
import { assetIsPreviewable } from "@/components/preview/utils";

type DataAsset = DataAssetWithManifestAndRealAssetDeploymentResponse;
type DataAssetPage =
  SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_;
type Direction = "previous" | "next";

const getAssetKey = (dataAsset: DataAsset | null | undefined) =>
  dataAsset?.public_id || dataAsset?.path || "";

const findPreviewableAsset = (
  items: DataAsset[],
  startIndex: number,
  step: 1 | -1
) => {
  for (
    let index = startIndex;
    index >= 0 && index < items.length;
    index += step
  ) {
    if (assetIsPreviewable(items[index]).canPreview) {
      return items[index];
    }
  }

  return null;
};

export default function useDataAssetPreviewNavigator({
  data,
  queryParams,
  totalItems,
}: {
  data: DataAssetPage | undefined;
  queryParams: Record<string, unknown>;
  totalItems?: number;
}) {
  const [selectedAsset, setSelectedAsset] = useState<DataAsset | null>(null);
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const [selectedPageItems, setSelectedPageItems] = useState<DataAsset[]>([]);
  const [initialPreviewUrl, setInitialPreviewUrl] = useState<string | null>(
    null
  );
  const [lastOpenedAssetId, setLastOpenedAssetId] = useState<string | null>(
    null
  );

  const pageSize = data?.size || Number(queryParams.size) || 10;
  const activePage =
    selectedPage || data?.page || Number(queryParams.page) || 1;
  const selectedAssetKey = getAssetKey(selectedAsset);

  const selectedIndex = useMemo(
    () =>
      selectedPageItems.findIndex(
        (item) => getAssetKey(item) === selectedAssetKey
      ),
    [selectedAssetKey, selectedPageItems]
  );

  const selectAsset = useCallback(
    ({
      dataAsset,
      page,
      pageItems,
      initialUrl = null,
    }: {
      dataAsset: DataAsset;
      page: number;
      pageItems: DataAsset[];
      initialUrl?: string | null;
    }) => {
      setSelectedAsset(dataAsset);
      setSelectedPage(page);
      setSelectedPageItems(pageItems);
      setInitialPreviewUrl(initialUrl);
      setLastOpenedAssetId(getAssetKey(dataAsset));
    },
    []
  );

  const openPreview = useCallback(
    (dataAsset: DataAsset, initialUrl?: string | null) => {
      selectAsset({
        dataAsset,
        page: data?.page || Number(queryParams.page) || 1,
        pageItems: data?.items || [dataAsset],
        initialUrl,
      });
    },
    [data?.items, data?.page, queryParams.page, selectAsset]
  );

  const closePreview = useCallback(() => {
    setSelectedAsset(null);
    setInitialPreviewUrl(null);
  }, []);

  const movePreview = useCallback(
    (direction: Direction) => {
      if (!selectedAsset) {
        return;
      }

      const step = direction === "next" ? 1 : -1;
      const localCandidate = findPreviewableAsset(
        selectedPageItems,
        selectedIndex + step,
        step
      );

      if (localCandidate) {
        selectAsset({
          dataAsset: localCandidate,
          page: activePage,
          pageItems: selectedPageItems,
        });
      }
    },
    [activePage, selectAsset, selectedAsset, selectedIndex, selectedPageItems]
  );

  const hasPrevious =
    selectedIndex > 0 &&
    !!findPreviewableAsset(selectedPageItems, selectedIndex - 1, -1);
  const hasNext =
    selectedIndex >= 0 &&
    !!findPreviewableAsset(selectedPageItems, selectedIndex + 1, 1);
  const hasNextPage =
    typeof totalItems === "number" ? activePage * pageSize < totalItems : false;
  const previousUnavailableMessage =
    selectedIndex >= 0 && !hasPrevious && activePage > 1
      ? "No more previewable files on this page. Close the preview and use Previous Page to continue."
      : undefined;
  const nextUnavailableMessage =
    selectedIndex >= 0 && !hasNext && hasNextPage
      ? "No more previewable files on this page. Close the preview and use Next Page to continue."
      : undefined;

  return {
    selectedAsset,
    initialPreviewUrl,
    isPreviewOpen: !!selectedAsset,
    lastOpenedAssetId,
    openPreview,
    closePreview,
    goToPreviousPreview: () => movePreview("previous"),
    goToNextPreview: () => movePreview("next"),
    hasPrevious,
    hasNext,
    previousUnavailableMessage,
    nextUnavailableMessage,
    navigatingDirection: null,
  };
}
